// apps/frontend/src/components/cart/CartIndicator.tsx
// 🛒 Indicateur Panier Ultra-Premium - Badge Header UPDATED
// Compatible avec votre nouveau hook useCart
// Version: 3.0.0 | Sprint: 5B | Quality: Enterprise Premium

import React, { memo } from 'react';
import { ShoppingBag, AlertCircle } from 'lucide-react';
import { useCart } from '../../hooks/useCart'; // 🆕 NOUVEAU HOOK
import { useTranslation } from '@mode-app/shared';
import { cn } from '../../utils/cn'; // Votre utilitaire existant

// ============================================================================
// 🎯 TYPES COMPONENT
// ============================================================================

interface CartIndicatorProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showPrice?: boolean;
  onClick?: () => void;
  variant?: 'header' | 'floating' | 'sidebar';
}

// ============================================================================
// 🛒 COMPOSANT PRINCIPAL
// ============================================================================

export const CartIndicator: React.FC<CartIndicatorProps> = memo(
  ({
    className,
    size = 'md',
    showLabel = false,
    showPrice = false,
    onClick,
    variant = 'header',
  }) => {
    // 🆕 NOUVEAU HOOK USECART
    const { isEmpty, isLoading, hasWarnings, helpers } = useCart();

    const { t } = useTranslation();

    // 🆕 NOUVELLES DONNÉES DÉRIVÉES
    const itemsCount = helpers.getTotalItems();
    const totalPrice = helpers.getTotalPrice();
    const vendorCount = helpers.getVendorCount();
    const hasUnavailableItems = helpers.hasUnavailableItems();
    const hasPriceChanges = helpers.hasPriceChanges();
    const hasUnreadyVendors = helpers.hasUnreadyVendors();

    // ============================================================================
    // 🎨 STYLES DYNAMIQUES
    // ============================================================================

    const sizeStyles = {
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-base',
      lg: 'w-12 h-12 text-lg',
    };

    const badgeSize = {
      sm: 'w-4 h-4 text-xs',
      md: 'w-5 h-5 text-xs',
      lg: 'w-6 h-6 text-sm',
    };

    const iconSize = {
      sm: 16,
      md: 20,
      lg: 24,
    };

    const variantStyles = {
      header:
        'relative inline-flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer',
      floating:
        'fixed bottom-6 right-6 z-50 shadow-2xl text-white rounded-full transition-all duration-300',
      sidebar:
        'w-full flex items-center justify-between p-3 rounded-lg bg-brand-offwhite hover:bg-brand-light transition-colors cursor-pointer',
    };

    // 🆕 COULEURS DYNAMIQUES SELON L'ÉTAT
    const getBackgroundColor = () => {
      if (variant === 'floating') {
        if (hasUnreadyVendors) return 'bg-brand-500 hover:bg-[#8b7355]';
        if (hasWarnings) return 'bg-brand-500 hover:bg-brand-sage';
        return 'bg-brand-500 hover:bg-brand-sage';
      }

      if (hasUnreadyVendors) return 'bg-brand-light hover:bg-[#e8d9c2]';
      if (hasWarnings) return 'bg-brand-light hover:bg-brand-light';
      return 'bg-brand-light hover:bg-brand-border';
    };

    // ============================================================================
    // 🔧 COMPUTED VALUES
    // ============================================================================

    const displayCount = itemsCount > 99 ? '99+' : itemsCount.toString();
    const hasAnyWarnings =
      hasWarnings ||
      hasUnavailableItems ||
      hasPriceChanges ||
      hasUnreadyVendors;

    // ============================================================================
    // 🎨 RENDU CONDITIONNEL PAR VARIANT
    // ============================================================================

    if (variant === 'sidebar') {
      return (
        <div
          className={cn(variantStyles[variant], className)}
          onClick={onClick}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && onClick?.()}
        >
          <div className="flex items-center space-x-3">
            <div className="relative">
              <ShoppingBag className="h-5 w-5 text-brand-warm" />
              {!isEmpty && (
                <div className="absolute -top-2 -right-2 rounded-full w-4 h-4 flex items-center justify-center text-xs font-semibold text-white bg-brand-500">
                  {displayCount}
                </div>
              )}
            </div>

            <div className="flex-1">
              <p className="font-medium text-brand-dark">
                {t('cart.title', { defaultValue: 'Mon panier' })}
              </p>
              {!isEmpty ? (
                <div className="space-y-1">
                  <p className="text-sm text-brand-warm">
                    {t('cart.itemCount', {
                      count: itemsCount,
                      defaultValue: `${itemsCount} article${itemsCount > 1 ? 's' : ''}`,
                    })}
                    {vendorCount > 1 && (
                      <span className="text-brand-warm">
                        {' • '}
                        {t('cart.vendorCount', {
                          count: vendorCount,
                          defaultValue: `${vendorCount} vendeur${vendorCount > 1 ? 's' : ''}`,
                        })}
                      </span>
                    )}
                  </p>
                  {/* 🆕 STATUT VINTED-LIKE */}
                  {hasUnreadyVendors && (
                    <p className="text-xs text-brand-warm flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Configuration livraison requise
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-brand-warm">
                  {t('cart.empty', { defaultValue: 'Votre panier est vide' })}
                </p>
              )}
            </div>

            {showPrice && !isEmpty && (
              <div className="text-right">
                <p className="font-bold text-brand-500">
                  {helpers.formatPrice(totalPrice)}
                </p>
                {hasUnreadyVendors && (
                  <p className="text-xs text-brand-warm">Frais à calculer</p>
                )}
              </div>
            )}
          </div>

          {hasAnyWarnings && !hasUnreadyVendors && (
            <div className="mt-2 flex items-center space-x-1 text-brand-warm">
              <AlertCircle className="h-3 w-3" />
              <span className="text-xs">
                {hasUnavailableItems &&
                  t('cart.warnings.itemUnavailable', {
                    defaultValue: 'Articles indisponibles',
                  })}
                {hasPriceChanges &&
                  t('cart.warnings.priceChanged', {
                    defaultValue: 'Prix modifiés',
                  })}
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative">
        {/* Bouton principal */}
        <button
          className={cn(
            variantStyles[variant],
            sizeStyles[size],
            getBackgroundColor(),
            className
          )}
          onClick={onClick}
          disabled={isLoading}
          aria-label={
            isEmpty
              ? t('cart.empty', { defaultValue: 'Panier vide' })
              : t('cart.itemCount', {
                  count: itemsCount,
                  defaultValue: `${itemsCount} article${itemsCount > 1 ? 's' : ''}`,
                })
          }
        >
          {/* Icône panier */}
          <ShoppingBag
            className={cn(
              isLoading ? 'animate-pulse text-brand-muted' : 'text-brand-dark',
              variant === 'floating' && 'text-white'
            )}
            size={iconSize[size]}
          />

          {/* Badge compteur avec couleurs dynamiques */}
          {!isEmpty && (
            <div
              className={cn(
                'absolute -top-2 -right-2 flex items-center justify-center rounded-full font-semibold',
                'text-white border-2 border-white',
                'animate-in zoom-in-50 duration-200',
                badgeSize[size]
              )}
              style={{
                backgroundColor: hasUnreadyVendors
                  ? '#f97316'
                  : hasAnyWarnings
                    ? '#eab308'
                    : 'var(--brand-500)',
              }}
            >
              {displayCount}
            </div>
          )}

          {/* 🆕 INDICATEURS ÉTAT VINTED-LIKE */}
          {hasUnreadyVendors && !isEmpty && (
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-brand-500 rounded-full border border-white animate-pulse" />
          )}
          {hasAnyWarnings && !hasUnreadyVendors && !isEmpty && (
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-brand-500 rounded-full border border-white animate-pulse" />
          )}
        </button>

        {/* Label optionnel */}
        {showLabel && (
          <div className="mt-1 text-center">
            <p className="text-xs font-medium text-brand-dark">
              {t('cart.title', { defaultValue: 'Panier' })}
            </p>
            {showPrice && !isEmpty && (
              <div>
                <p className="text-xs font-bold text-brand-500">
                  {helpers.formatPrice(totalPrice)}
                </p>
                {hasUnreadyVendors && (
                  <p className="text-xs text-brand-warm">+ frais</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tooltip pour floating variant */}
        {variant === 'floating' && !isEmpty && (
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {t('cart.itemCount', {
              count: itemsCount,
              defaultValue: `${itemsCount} article${itemsCount > 1 ? 's' : ''}`,
            })}
            {showPrice && (
              <span className="block font-semibold">
                {helpers.formatPrice(totalPrice)}
              </span>
            )}
            {/* 🆕 STATUT TOOLTIP */}
            {hasUnreadyVendors && (
              <span className="block text-[#e8d9c2] text-xs">
                Configuration requise
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

CartIndicator.displayName = 'CartIndicator';

// ============================================================================
// 📊 VARIANTS SPÉCIALISÉS
// ============================================================================

export const HeaderCartIndicator: React.FC<
  Omit<CartIndicatorProps, 'variant'>
> = props => <CartIndicator variant="header" {...props} />;

export const FloatingCartIndicator: React.FC<
  Omit<CartIndicatorProps, 'variant'>
> = props => <CartIndicator variant="floating" {...props} />;

export const SidebarCartIndicator: React.FC<
  Omit<CartIndicatorProps, 'variant'>
> = props => <CartIndicator variant="sidebar" showPrice {...props} />;

export default CartIndicator;
